import { getUrlParams } from '@/utils/base';
import { intersection } from 'lodash-es';
import { authCheckGetLoginInfo } from '@/api/wallet';
import { logoutToLogin } from '@/utils/wallet';
// 注意：crypto 解密仅在 cwBootstrap 带 order 时需要，按需 dynamic import（见下方 walletInfoInit），
// 避免 crypto-vendor 进入钱包首屏关键路径。

// 支付方式图标
import iconBank from '@/assets/pay-icons/bank.png';
import iconWechat from '@/assets/pay-icons/wechat.png';
import iconAlipay from '@/assets/pay-icons/alipay.png';
import iconQQ from '@/assets/pay-icons/qq.png';
import iconDigital from '@/assets/pay-icons/digital-rmb.png';

// 钱包信息
let walletInfo = null;

// 钱包是否全屏
let isWalletFullScreen = false;

// 会员信息
let memberInfo = null;

// 钱包初始化是否完成
let isWalletInitFinished = false;

export const getIsWalletInitFinished = () => isWalletInitFinished;
export const setIsWalletInitFinished = val => {
  isWalletInitFinished = val;
};

const decodeCounterQueryParam = value => {
  if (value === undefined || value === null) return '';
  try {
    return decodeURIComponent(String(value).replace(/\+/g, '%20'));
  } catch {
    return String(value);
  }
};

// 收银台跳钱包充值拉单参数
export const counterToWalletDepostParamsRC = () => {
  const rawSearch = sessionStorage.getItem('counterToWalletDepostParams');
  if (!rawSearch) return null;

  return rawSearch.split('&').reduce((result, item) => {
    if (!item) return result;

    const [rawKey, ...rawValueParts] = item.split('=');
    const key = decodeCounterQueryParam(rawKey);
    if (!key) return result;

    result[key] = decodeCounterQueryParam(rawValueParts.join('='));
    return result;
  }, {});
};

/** cwBootstrap + 68C_WALLET：merchantPlaceWallet 进行中为 pending；成功后 ok；失败（含 7016）为 failed，避免提前弹安全密码 */
export const CW_BOOTSTRAP_PLACE_WALLET_GATE_KEY = 'cwBootstrapPlaceWalletGate';

/**
 * cwBootstrap：dynamic import / 换票完成前同步写入，防止 CounterDepositWallet 误用旧拉单参数弹出安全密码
 */
export const CW_BOOTSTRAP_BLOCK_COUNTER_DEPOSIT_PW_KEY = 'cwBootstrapBlockCounterDepositPassword';

/** cwBootstrap 换票后已在 mine 前 prefetch 会员+余额：仅 mine 主路径 walletInitFinished 跳过重复请求；普通登录/嵌入页不走 cwBootstrap 则无此标记 */
export const CW_BOOTSTRAP_MINE_HEAD_PREFETCHED_SESSION_KEY = 'cwBootstrapMineHeadPrefetched';

/**
 * cwBootstrap：member+balance prefetch 进行中。walletInitFinished 往往早于首包返回，mine 据此勿再打 queryWalletBalance/queryMemberInfo 重复请求。
 */
export const CW_BOOTSTRAP_MINE_HEAD_PREFETCH_IN_FLIGHT_KEY = 'cwBootstrapMineHeadPrefetchInFlight';

/** cwBootstrap：mine 刷新 icon 开始时间戳（ms），用于「至少转 1s、未满则补到 1s、超过则接口结束即停」；退出登录时清除 */
export const CW_BOOTSTRAP_MINE_BALANCE_SPIN_STARTED_AT_KEY = 'cwBootstrapMineBalanceSpinStartedAt';

export const isCwBootstrapBlockingCounterDepositPassword = () => {
  try {
    return sessionStorage.getItem(CW_BOOTSTRAP_BLOCK_COUNTER_DEPOSIT_PW_KEY) === '1';
  } catch {
    return false;
  }
};

export const setCwBootstrapPlaceWalletGatePending = () => {
  try {
    sessionStorage.setItem(CW_BOOTSTRAP_PLACE_WALLET_GATE_KEY, 'pending');
  } catch {}
};

export const setCwBootstrapPlaceWalletGateOk = () => {
  try {
    sessionStorage.setItem(CW_BOOTSTRAP_PLACE_WALLET_GATE_KEY, 'ok');
  } catch {}
};

export const setCwBootstrapPlaceWalletGateFailed = () => {
  try {
    sessionStorage.setItem(CW_BOOTSTRAP_PLACE_WALLET_GATE_KEY, 'failed');
  } catch {}
};

export const clearCwBootstrapPlaceWalletGate = () => {
  try {
    sessionStorage.removeItem(CW_BOOTSTRAP_PLACE_WALLET_GATE_KEY);
  } catch {}
};

export const getCwBootstrapPlaceWalletGate = () => {
  try {
    return sessionStorage.getItem(CW_BOOTSTRAP_PLACE_WALLET_GATE_KEY);
  } catch {
    return null;
  }
};

/** 历史遗留 key：logout 时顺带清除；过期关窗已改用 {@link CASHIER_PAYMENT_LOADING_TO_WALLET_TAB_KEY} */
export const CASHIER_COUNTER_TOKEN_WALLET_TAB_KEY = 'cashierCounterTokenWalletTab';

/**
 * 仅由收银台 /counter/payment-loading 在 replace 进钱包前写入（同 origin 时）。
 * 跨域进钱包时 sessionStorage 不可共享，需在 URL 上带 {@link CASHIER_PAYMENT_LOADING_URL_QUERY_KEY}，由 walletInfoInit 再写入 sessionStorage。
 */
export const CASHIER_PAYMENT_LOADING_TO_WALLET_TAB_KEY = 'cashierPaymentLoadingToWalletTab';

/** payment-loading → 钱包跳转 URL 查询参数，值为 `1` 时表示经收银台中转，过期应关窗（与 opener 无关） */
export const CASHIER_PAYMENT_LOADING_URL_QUERY_KEY = 'cplt';

/** URL 带 `cplt`（收银台中转/cwBootstrap 同源标记）：换票完成前勿自动弹登录框 */
export const hasCashierFlowCpltUrlQuery = () => {
  if (typeof window === 'undefined') return false;
  const v = getUrlParams()[CASHIER_PAYMENT_LOADING_URL_QUERY_KEY];
  return v !== undefined && v !== null && String(v).trim() !== '';
};

export const markCashierPaymentLoadingToWalletTab = () => {
  try {
    sessionStorage.setItem(CASHIER_PAYMENT_LOADING_TO_WALLET_TAB_KEY, '1');
  } catch {}
};

/** 仅当经 payment-loading 写入 {@link CASHIER_PAYMENT_LOADING_TO_WALLET_TAB_KEY}（含 URL `cplt=1` 进钱包后初始化）时为 true */
export const isCashierCounterTokenWalletBrowserTabExpireShouldClose = () => {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(CASHIER_PAYMENT_LOADING_TO_WALLET_TAB_KEY) === '1';
};

/**
 * 收支方式列表
 */
export const payments = [
  {
    title: '银行卡',
    tradeType: 1,
    icon: iconBank,
  },
  {
    title: '微信',
    tradeType: 2,
    icon: iconWechat,
  },
  {
    title: '支付宝',
    tradeType: 3,
    icon: iconAlipay,
  },
  {
    title: 'QQ钱包',
    tradeType: 4,
    icon: iconQQ,
  },
  {
    title: '数字人民币',
    tradeType: 5,
    icon: iconDigital,
  },
];

/**
 * 获取支付方式显示名称（payTypeName 去「数据中心」前缀等）
 * @param {{ skipAccountTypeName?: boolean }} [opts] 挂单/购买等与明细对齐时不优先 accountTypeName
 */
export const getDisplayTypeName = (info, opts) => {
  if (!opts?.skipAccountTypeName && info?.accountTypeName) {
    return info.accountTypeName;
  }

  if (info?.customTypeName) {
    return info.customTypeName;
  }

  if (info?.payTypeName?.startsWith('数据中心')) {
    return info.payTypeName.slice(4);
  }

  return info?.payTypeName;
};

const COUNTER_LOGIN_REQUIRED_KEYS = ['counterToken', 'mchId', 'sourceMemberId', 'snowId', 'walletMemberId', 'memberId', 'sceneType', 'type'];

/**
 * 根据收银台 jumpToOrderUrl 返回的完整 URL（含查询串）解析参数并换票登录（不写 window.location）。
 */
export const walletLoginFromJumpOrderUrl = async jumpUrlString => {
  if (!jumpUrlString || typeof jumpUrlString !== 'string') {
    return '打开失败';
  }
  let urlObj;
  try {
    urlObj = new URL(jumpUrlString, typeof window !== 'undefined' ? window.location.origin : undefined);
  } catch {
    return '链接无效';
  }
  const urlParams = Object.fromEntries(urlObj.searchParams.entries());
  const depositParamsQuery = urlObj.search.startsWith('?') ? urlObj.search.slice(1) : urlObj.search;

  return completeWalletLoginFromCounterUrlParams(urlParams, depositParamsQuery);
};

async function completeWalletLoginFromCounterUrlParams(urlParams, depositParamsQueryString) {
  if (intersection(COUNTER_LOGIN_REQUIRED_KEYS, Object.keys(urlParams)).length !== COUNTER_LOGIN_REQUIRED_KEYS.length) {
    return '访问错误';
  }

  // 须在换票接口之前写入：否则 getLoginInfo 直接 6102/6326 时拦截器误判为「非收银台中转」而去 mine 弹登录
  try {
    if (String(urlParams[CASHIER_PAYMENT_LOADING_URL_QUERY_KEY] || '') === '1') {
      markCashierPaymentLoadingToWalletTab();
    }
  } catch {
    //
  }

  let res;
  try {
    res = await authCheckGetLoginInfo({
      counterToken: urlParams.counterToken,
      snowId: urlParams.snowId,
    });
  } catch (e) {
    console.error('authCheckGetLoginInfo error:', e);
    return '初始化失败，请重新访问';
  }

  const { sceneType, time, type } = urlParams;
  const { accessToken, mchId, userType, sourceMemberId, randomKey, walletMemberId } = res;

  walletInfo = {
    accessToken,
    mchId: Number(mchId),
    userType: Number(userType),
    sourceMemberId,
    randomKey,
    walletMemberId: Number(walletMemberId),
    sceneType,
    time,
    type,
  };

  try {
    if (Number(sceneType) === 4) {
      sessionStorage.setItem('counterToWalletDepostParams', depositParamsQueryString);
    }
    sessionStorage.setItem('innerWalletLogined', 'true');
    localStorage.setItem('walletInfo', JSON.stringify(walletInfo));
  } catch (storageError) {
    console.warn('Failed to save to localStorage:', storageError);
  }

  // await replaceWalletUrlAfterCashierGetLoginInfoSuccess();
  return '';
}

/**
 * 初始化钱包信息
 */
export const walletInfoInit = async () => {
  try {
    // 获取url参数
    const urlParams = getUrlParams();

    // 必需有的参数
    const requiredKeys = ['counterToken', 'mchId', 'sourceMemberId', 'snowId', 'walletMemberId', 'memberId', 'sceneType', 'type'];

    const { counterToken } = urlParams;

    // 参数中没有counterToken，则从本地缓存中获取钱包信息
    if (!counterToken) {
      try {
        // 浏览器：收银台直达钱包域名；68C_WALLET 为先 jumpToOrderUrl 换票登录再 merchantPlaceWallet（无 document.replace）
        if (String(urlParams.cwBootstrap || '') === '1' && urlParams.order) {
          let decrypted;
          try {
            const { ecbDecrypt } = await import('@/utils/encrypt');
            decrypted = JSON.parse(await ecbDecrypt(urlParams.order));
          } catch {
            return '链接无效';
          }
          if (String(urlParams[CASHIER_PAYMENT_LOADING_URL_QUERY_KEY] || '') === '1') {
            markCashierPaymentLoadingToWalletTab();
          }
          const deferCounterDepositPasswordDuringCwBootstrap = decrypted?.typeCode === '68C_WALLET' || decrypted?.typeCode === '68C_WALLET_ENTER';
          if (deferCounterDepositPasswordDuringCwBootstrap) {
            try {
              sessionStorage.setItem(CW_BOOTSTRAP_BLOCK_COUNTER_DEPOSIT_PW_KEY, '1');
            } catch {}
          }

          walletInfoRC(null);
          try {
            const { runWallet68cCrossBootstrap } = await import('@/utils/wallet/wallet-68c-cross-bootstrap');
            const errMsg = await runWallet68cCrossBootstrap(decrypted, urlParams);
            if (errMsg) return errMsg;
            return '';
          } finally {
            if (deferCounterDepositPasswordDuringCwBootstrap) {
              try {
                sessionStorage.removeItem(CW_BOOTSTRAP_BLOCK_COUNTER_DEPOSIT_PW_KEY);
              } catch {}
            }
          }
        }

        // 兼容 /wallet-embedded 下由外部直接带 accessToken 等登录信息的入口，不影响普通 /wallet 页面
        const embeddedDirectKeys = ['accessToken', 'mchId', 'sourceMemberId', 'randomKey', 'walletMemberId'];
        const isWalletEmbedded = location.pathname.indexOf('/wallet-embedded/') === 0;
        if (isWalletEmbedded && embeddedDirectKeys.every(key => urlParams[key] !== undefined && urlParams[key] !== null && String(urlParams[key]) !== '')) {
          const fromUrl = {
            accessToken: urlParams.accessToken,
            mchId: Number(urlParams.mchId),
            userType: Number(urlParams.userType !== undefined && urlParams.userType !== null && urlParams.userType !== '' ? urlParams.userType : 1),
            sourceMemberId: urlParams.sourceMemberId,
            randomKey: urlParams.randomKey,
            walletMemberId: Number(urlParams.walletMemberId),
            sceneType: urlParams.sceneType,
            time: urlParams.time,
            type: urlParams.type,
          };

          walletInfoRC(fromUrl);
          sessionStorage.setItem('innerWalletLogined', 'true');

          if (Number(fromUrl.sceneType) === 2 && Number(fromUrl.type) === 38) {
            localStorage.setItem(
              'authorizedPopupInfo',
              JSON.stringify({
                inMemberId: urlParams.memberId,
                inSourceMemberId: urlParams.sourceMemberId,
                mchId: urlParams.mchId,
              })
            );
          }

          if (Number(fromUrl.sceneType) === 4) {
            sessionStorage.setItem('counterToWalletDepostParams', window.location.search.slice(1));
          }

          return '';
        }

        const walletInfoStr = localStorage.getItem('walletInfo');
        const values = walletInfoStr ? JSON.parse(walletInfoStr) : null;

        // 存储授权登录信息，后续授权绑定弹窗时需要使用
        if (Number(urlParams.sceneType) === 2 && Number(urlParams.type) === 38) {
          localStorage.setItem(
            'authorizedPopupInfo',
            JSON.stringify({
              inMemberId: urlParams.memberId,
              inSourceMemberId: urlParams.sourceMemberId,
              mchId: urlParams.mchId,
            })
          );
        }

        // 如果当前登录状态是内部钱包，且是首次打开窗口不是刷新，则退出内部钱包登录
        if (values && Number(values.userType) === 1 && !sessionStorage.getItem('innerWalletLogined')) {
          logoutToLogin();
          return '请重新登录';
        }

        if (values) {
          walletInfo = values;
          return '';
        }
      } catch (storageError) {
        console.warn('localStorage access failed:', storageError);
        return '存储访问失败，请重新访问';
      }

      return '访问过期，请重新访问';
    }

    // 如果url参数中包含必需的参数，则设置counterInfo
    // 旧链接直达换票：await getLoginInfo 前先清空（cwBootstrap 新链已在 order 分支 walletInfoRC(null)，此处避免 completeWallet 内重复清空）
    if (intersection(requiredKeys, Object.keys(urlParams)).length === requiredKeys.length) {
      const depositParamsQuery = typeof window !== 'undefined' ? window.location.search.slice(1) : '';
      walletInfoRC(null);
      return completeWalletLoginFromCounterUrlParams(urlParams, depositParamsQuery);
    }

    // 如果url参数中不包含必需的参数，则返回false
    return '访问错误';
  } catch (error) {
    console.error('walletInfoInit error:', error);
    return '初始化失败，请重新访问';
  }
};

/**
 * 获取钱包信息
 */
export const walletInfoRC = data => {
  // 如果数据为null，则清空钱包信息
  if (data === null) {
    // 清空钱包信息
    walletInfo = null;

    // 清空本地缓存
    localStorage.removeItem('walletInfo');
    // 内嵌订单通知「已展示」去重（与 global.vue WALLET_EMBEDDED_SHOWN_ORDER_NOTICE_KEYS 一致）；换号/清登录后不应沿用旧 key
    try {
      localStorage.removeItem('walletEmbeddedShownOrderNoticeKeys');
    } catch {
      //
    }
  } else if (data) {
    // 设置钱包信息
    walletInfo = data;

    // 数据记录到本地
    localStorage.setItem('walletInfo', JSON.stringify(walletInfo));
  } else if (!walletInfo) {
    // 从本地缓存中获取
    const walletInfoStr = localStorage.getItem('walletInfo');

    // 从本地缓存中获取钱包信息
    const values = walletInfoStr ? JSON.parse(walletInfoStr) : null;

    // 如果有钱包信息，则设置钱包信息
    if (values) {
      walletInfo = values;
    }
  }

  return walletInfo ? { ...walletInfo, plat: location.pathname.indexOf('/wallet-embedded') === 0 ? 1 : 2 } : null;
};

const WALLET_BH_KEY = 'walletBh';

/**
 * 与 layout/wallet.vue 一致：是否全屏（当前 URL 的 bh 优先，否则 walletBh）。
 * 每次从页面读取，跳聊天室时用此值可避免与 isWalletFullScreenRC 模块内存不一致。
 */
export const isWalletFullScreenFromUrlOrStorage = () => {
  const urlParams = getUrlParams();
  const bhFromUrl = urlParams.bh;
  const bhRaw = bhFromUrl ?? localStorage.getItem(WALLET_BH_KEY);
  return bhRaw === '1' || bhRaw === 'true';
};

/**
 * 获取/设置钱包是否全屏（永久存储 localStorage）
 */
export const isWalletFullScreenRC = value => {
  if (value !== undefined && value !== null) {
    isWalletFullScreen = !!value;
    localStorage.setItem(WALLET_BH_KEY, String(isWalletFullScreen));
  } else if (isWalletFullScreen === false) {
    const stored = localStorage.getItem(WALLET_BH_KEY);
    isWalletFullScreen = stored === 'true' || stored === '1';
  }
  return isWalletFullScreen;
};

/**
 * 会员信息 读写
 */
export const memberInfoRC = (data, isMerge) => {
  // 如果数据为null，则清空会员信息
  if (data === null) {
    // 清空会员信息
    memberInfo = null;

    // 清空本地缓存
    localStorage.removeItem('memberInfo');

    return null;
  }

  // 如果数据存在，则设置会员信息
  if (data) {
    // 如果需要合并数据，则合并数据,否则直接设置数据
    memberInfo = isMerge ? { ...memberInfo, ...data } : data;

    // 数据记录到本地
    localStorage.setItem('memberInfo', JSON.stringify(memberInfo));
  } else if (!memberInfo) {
    // 从本地缓存中获取会员信息
    const memberInfoStr = localStorage.getItem('memberInfo');

    // 从本地缓存中获取会员信息
    memberInfo = memberInfoStr ? JSON.parse(memberInfoStr) : null;
  }

  // 返回会员信息
  return memberInfo;
};

/**
 * 是否是外部钱包
 * userType 1: 内部钱包 2: 外部钱包
 * @returns {boolean}
 */
export const isExternalWallet = () => {
  const walletInfo = walletInfoRC();
  return walletInfo && Number(walletInfo.userType) === 2;
};

/** 是否 68 App 内嵌壳（/wallet-embedded） */
export const isWalletEmbeddedShell = () => typeof location !== 'undefined' && location.pathname.indexOf('/wallet-embedded') === 0;

/**
 * 设置主题颜色
 * @param {string} color 主题颜色
 */
export const setWalletThemeColor = color => {
  let metaThemeColor = document.querySelector('meta[name="theme-color"]');

  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', color);
  } else {
    metaThemeColor = document.createElement('meta');
    metaThemeColor.setAttribute('name', 'theme-color');
    metaThemeColor.setAttribute('content', color);
    document.head.appendChild(metaThemeColor);
  }
};

// 余额的读写
let balance = {};

/**
 * 余额的读写
 */
export const walletBalanceRC = value => {
  if (value) {
    balance = { ...balance, ...value };
    localStorage.setItem('balance', JSON.stringify(balance));
  } else if (balance === undefined) {
    balance = JSON.parse(localStorage.getItem('balance') || '{}');
    return balance;
  }
  return balance || {};
};
